// Hook personnalisé pour gérer l'état de subscription
// Aligné avec US-PAY-5: Gestion d'État Subscription Frontend

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { auth, db, app } from '../firebase';
import { doc, collection, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
// NOTE: `firebase/functions` (~30-50KB) est importé DYNAMIQUEMENT dans les
// callbacks `upgrade`/`manageBilling` uniquement. Ce hook est monté eager au
// boot (SubscriptionProvider + ProtectedRoute) : un import statique tirerait
// `firebase/functions` dans le chunk initial alors qu'il n'est utilisé qu'au
// clic d'un CTA de checkout/billing. L'abonnement lui-même se lit via
// onSnapshot Firestore (firebase/firestore, déjà au boot) — inchangé.
import {
  SubscriptionState,
  SubscriptionTier,
  SubscriptionStatus,
  SubscriptionFeatures,
  SubscriptionUsage,
  SubscriptionBilling,
  LicenseInfo,
  LicenseOverride,
} from '../types/subscription.types';
import { Timestamp } from 'firebase/firestore';
import {
  trackSubscriptionCreated,
  trackCheckoutStarted,
  trackTrialStarted,
  trackCheckoutSessionCreateAttempted,
  trackCheckoutSessionCreateSucceeded,
  trackCheckoutSessionCreateFailed,
  getStoredAttribution,
} from '../utils/analytics';
import { computeLicense, computeFeatures } from './subscriptionEntitlement';
// Re-export SubscriptionState for convenience
export type { SubscriptionState };

export const useSubscription = (): SubscriptionState => {
  const [tier, setTier] = useState<SubscriptionTier>('free');
  const [status, setStatus] = useState<SubscriptionStatus | 'loading'>('loading');
  const [cvCount, setCvCount] = useState<number>(0);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | undefined>();
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>(null); // Nouveau: stocker le rôle
  const [hasHadTrial, setHasHadTrial] = useState<boolean>(false);
  const [licenseOverride, setLicenseOverride] = useState<LicenseOverride | null>(null);
  const [hasStripeCustomerId, setHasStripeCustomerId] = useState<boolean>(false);

  // Refs to store unsubscribe functions
  const subscriptionUnsubscribeRef = useRef<(() => void) | null>(null);
  const userUnsubscribeRef = useRef<(() => void) | null>(null);
  const draftsUnsubscribeRef = useRef<(() => void) | null>(null);
  // Track previous subscription status to detect transitions and fire GA4 events once
  const prevSubscriptionStatusRef = useRef<string | null>(null);

  useEffect(() => {
    // Cleanup function for Firestore listeners
    const cleanupListeners = () => {
      if (subscriptionUnsubscribeRef.current) {
        try {
          subscriptionUnsubscribeRef.current();
        } catch {
          // Ignore cleanup errors
        }
        subscriptionUnsubscribeRef.current = null;
      }
      if (userUnsubscribeRef.current) {
        try {
          userUnsubscribeRef.current();
        } catch {
          // Ignore cleanup errors
        }
        userUnsubscribeRef.current = null;
      }
      if (draftsUnsubscribeRef.current) {
        try {
          draftsUnsubscribeRef.current();
        } catch { /* ignore */ }
        draftsUnsubscribeRef.current = null;
      }
    };

    // Écouter les changements d'authentification
    const unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
      // Cleanup previous Firestore listeners
      cleanupListeners();

      if (!user) {
        setTier('free');
        setStatus('inactive');
        setCvCount(0);
        setLoading(false);
        return;
      }

      // Écouter les changements de subscription en temps réel
      // SOURCE UNIQUE DE VÉRITÉ pour tier, status, cvCount, billing
      const subscriptionRef = doc(db, 'subscriptions', user.uid);
      const unsubscribeSubscription = onSnapshot(
        subscriptionRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setTier((data.tier as SubscriptionTier) || 'free');
          setStatus((data.status as SubscriptionStatus) || 'inactive');

          if (data.currentPeriodEnd) {
            const periodEnd = data.currentPeriodEnd.toDate();
            setCurrentPeriodEnd(periodEnd);
          }

          setCancelAtPeriodEnd(data.cancelAtPeriodEnd || false);

          // Vérifier si l'utilisateur a un vrai compte Stripe
          setHasStripeCustomerId(!!data.stripeCustomerId);

          // Vérifier si l'utilisateur a déjà eu un trial (complété par le user doc listener)
          const hadTrialFromSub = !!(data.trialEnd || data.tier === 'trial_7d');
          if (hadTrialFromSub) {
            setHasHadTrial(true);
          }

          // Google Ads conversion: trial_started fires from the client because
          // the Stripe webhook only writes Firestore analyticsEvents — it does
          // NOT ping googleadservices.com. The previous code used a prevStatus
          // transition guard, but prevStatus is null on first mount after the
          // Stripe redirect, so it never fired (regression from #965).
          // De-dup by stripeSubscriptionId via localStorage to allow a single
          // fire per trial across remounts / refreshes.
          if (data.status === 'trialing' && data.stripeSubscriptionId) {
            const trialFireKey = `trial_started_fired_${data.stripeSubscriptionId}`;
            try {
              if (!localStorage.getItem(trialFireKey)) {
                trackTrialStarted(data.tier || 'trial_7d', 0);
                localStorage.setItem(trialFireKey, '1');
              }
            } catch { /* private mode / quota — skip dedup, accept potential double-fire */ }
          }

          // GA4: Track subscription transitions (active = paid).
          const currentSubStatus = data.status as string;
          const prevStatus = prevSubscriptionStatusRef.current;
          if (prevStatus && prevStatus !== currentSubStatus) {
            if (currentSubStatus === 'active' && prevStatus !== 'active') {
              trackSubscriptionCreated(
                data.stripeSubscriptionId || user.uid,
                data.tier || 'unknown',
                data.price || 0
              );
            }
          }
          prevSubscriptionStatusRef.current = currentSubStatus;
        } else {
          // Pas de subscription active = plan gratuit
          // Note: hasHadTrial n'est PAS reset ici, il est géré par le user doc (flag permanent)
          setTier('free');
          setStatus('inactive');
          setCvCount(0);
          setHasStripeCustomerId(false);
        }
        setLoading(false);
      },
      () => {
        setStatus('inactive');
        setLoading(false);
      }
    );

    // Écouter le nombre réel de CV drafts éligibles au quota.
    // EXCLUSIONS — alignées avec checkQuota côté serveur (premiumGuard.ts) :
    //   - isMaster=true   : le CV principal ne consomme pas de quota
    //   - isTemporary=true : auto_tailored créés par l'autopilote ne saturent
    //                        pas la limite gratuite tout seuls
    const draftsRef = collection(db, 'users', user.uid, 'cvBuilder', 'data', 'drafts');
    const unsubscribeDrafts = onSnapshot(
      draftsRef,
      (snapshot) => {
        let eligible = 0;
        snapshot.forEach((d) => {
          const data = d.data();
          if (data.isMaster) return;
          if (data.isTemporary) return;
          eligible++;
        });
        setCvCount(eligible);
      },
      () => { /* ignore errors */ }
    );

    // Écouter le user doc pour rôle admin, licenseOverride et hasUsedTrial
    const userRef = doc(db, 'users', user.uid);
    const unsubscribeUser = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          // Vérifier si l'utilisateur est admin (rôles: admin, support, finance, tech)
          const role = data.role || null;
          setUserRole(role);

          // isAdmin = vrai pour tous les rôles d'administration
          const adminRoles = ['admin', 'support', 'finance', 'tech'];
          setIsAdmin(role ? adminRoles.includes(role) : false);

          // Flag permanent : l'utilisateur a-t-il déjà utilisé un trial ?
          if (data.hasUsedTrial === true) {
            setHasHadTrial(true);
          }

          // Lire licenseOverride depuis le user doc (override manuel par admin)
          if (data.licenseOverride) {
            const override = data.licenseOverride as LicenseOverride;
            // Vérifier si l'override n'est pas expiré
            if (override.enabled) {
              if (override.expiresAt) {
                const expiresAt = override.expiresAt.toDate();
                if (expiresAt > new Date()) {
                  setLicenseOverride(override);
                } else {
                  setLicenseOverride(null);
                }
              } else {
                setLicenseOverride(override);
              }
            } else {
              setLicenseOverride(null);
            }
          } else {
            setLicenseOverride(null);
          }
        }
      },
      () => {
        // Ignore user fetch errors
      }
    );

    // Store unsubscribe functions in refs
    subscriptionUnsubscribeRef.current = unsubscribeSubscription;
    userUnsubscribeRef.current = unsubscribeUser;
    draftsUnsubscribeRef.current = unsubscribeDrafts;

    }); // End of onAuthStateChanged callback

    // Cleanup on unmount
    return () => {
      unsubscribeAuth();
      cleanupListeners();
    };
  }, []);

  // Licence effective = SOURCE UNIQUE D'ENTITLEMENT côté frontend
  // (Admin role > Override admin > Stripe active/trialing). Logique pure dans
  // `subscriptionEntitlement.ts` (testée unitairement), alignée avec le hard
  // gate backend `validatePremiumAccess` (premiumGuard.ts). Toute décision
  // d'accès premium (paywall, FeatureGate, quotas, autopilote) DOIT dériver de
  // `license.isActive` / `license.tierEffective`, jamais du `tier` brut — sinon
  // un abonné en impayé (past_due/unpaid) garderait l'accès pro.
  const license: LicenseInfo = useMemo(
    () => computeLicense({ userRole, licenseOverride, tier, status, hasStripeCustomerId }),
    [userRole, licenseOverride, tier, status, hasStripeCustomerId]
  );

  // Features dérivées du TIER EFFECTIF de la licence (≠ tier brut Stripe) :
  // licence inactive (impayé, annulé, incomplet, pas d'abo) → plan gratuit.
  const features: SubscriptionFeatures = useMemo(
    () => computeFeatures(license, isAdmin),
    [license, isAdmin]
  );

  // Calculer l'usage
  const usage: SubscriptionUsage = useMemo(() => {
    const maxCVs = features.maxCVs;
    const remaining = maxCVs === -1 ? -1 : Math.max(0, maxCVs - cvCount);

    return {
      cvCount,
      cvsRemaining: remaining,
    };
  }, [cvCount, features.maxCVs]);

  // Calculer le billing
  const billing: SubscriptionBilling = useMemo(() => {
    let daysUntilRenewal: number | undefined;

    if (currentPeriodEnd) {
      const now = new Date();
      const diff = currentPeriodEnd.getTime() - now.getTime();
      daysUntilRenewal = Math.ceil(diff / (1000 * 60 * 60 * 24));
    }

    return {
      currentPeriodEnd,
      cancelAtPeriodEnd,
      daysUntilRenewal,
    };
  }, [currentPeriodEnd, cancelAtPeriodEnd]);

  // Actions
  const upgrade = useCallback(async (newTier: SubscriptionTier): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }

    // Canonical funnel event — fires from EVERY paywall surface that calls
    // `actions.upgrade` (PaywallGuard, FeatureGate, TrialExpiringBanner,
    // StripeCheckoutButton). Surfaces that call createCheckoutSession
    // directly (e.g. onboarding Step7) fire `checkout_started` themselves
    // with a more specific `source`. Source `other` here means "an in-app
    // paywall surface routed through useSubscription.actions.upgrade" — fine
    // for the top-of-funnel count, even if the exact CTA isn't attributed.
    trackCheckoutStarted('other', newTier);
    trackCheckoutSessionCreateAttempted(newTier, 'paywall', user.uid);
    const attemptedAt = Date.now();

    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions(app, 'europe-west1');
    const createCheckout = httpsCallable(functions, 'createCheckoutSession');

    // Forward the Google Ads click ids captured at landing so the Stripe
    // webhook can fire `trial_started` server-side via Measurement Protocol
    // (issue #1010). Adblockers strip the client gtag beacon for ~50% of
    // users, but the click id survives in sessionStorage from the landing
    // URL — propagating it through Stripe metadata makes the conversion
    // adblock-proof.
    const attribution = getStoredAttribution();

    try {
      // Pas de `promoCode` : 'FITD2025' était hard-codé ici alors qu'aucun
      // promotion code de ce nom n'existe dans Stripe. Effet réel : un appel
      // Stripe inutile par checkout, `metadata.promoCode` pollué sur toutes les
      // sessions — et une session qui aurait échoué en bloc le jour où ce code
      // aurait été créé (Stripe refuse `discounts` + `allow_promotion_codes`
      // ensemble). Une remise se pilote server-side, pas depuis le client.
      const result = await createCheckout({
        tier: newTier,
        gclid: attribution.gclid,
        gbraid: attribution.gbraid,
        wbraid: attribution.wbraid,
        successUrl: `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/pricing`,
      });

      const data = result.data as { url?: string; sessionId?: string };
      if (data?.url) {
        trackCheckoutSessionCreateSucceeded(data.sessionId ?? '', newTier, user.uid, Date.now() - attemptedAt);
        window.location.href = data.url;
      } else {
        throw new Error('URL de checkout non reçue de la Cloud Function');
      }
    } catch (err: any) {
      trackCheckoutSessionCreateFailed(err?.code || 'unknown', (err?.message || '').slice(0, 100), newTier, user.uid);
      throw err;
    }
  }, []);

  const manageBilling = useCallback(async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }

    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions(app, 'europe-west1');
    const createBillingPortal = httpsCallable(functions, 'createBillingPortalSession');

    const result = await createBillingPortal({});

    const data = result.data as { url?: string };
    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error('URL du billing portal non reçue de la Cloud Function');
    }
  }, []);

  const cancelSubscription = useCallback(async (): Promise<void> => {
    await manageBilling();
  }, [manageBilling]);

  return useMemo(() => ({
    tier: loading ? 'free' as SubscriptionTier : tier,
    status: loading ? 'loading' as const : status,
    features,
    usage,
    billing,
    hasHadTrial,
    license,
    actions: {
      upgrade,
      manageBilling,
      cancelSubscription,
    },
  }), [loading, tier, status, features, usage, billing, hasHadTrial, license, upgrade, manageBilling, cancelSubscription]);
};

// ⚠️ DEPRECATED: getStripePriceId removed
// Price IDs are now managed in Firebase Environment Configuration (backend)
// The createCheckoutSession Cloud Function accepts 'tier' parameter instead of 'priceId'
